import { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { Button } from '@/components/ui/button';
import { FullScreenDialog } from '@/components/ui/full-screen-dialog';
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemGroup,
  ItemTitle,
} from '@/components/ui/item';
import { Switch } from '@/components/ui/switch';
import { useResourceSets } from 'components/IAP/hooks/resourceSetHooks';
import { RuleEditorSteps } from 'components/MapPage/CorporateViewTabs/EventsTab/RuleEditorSteps';
import {
  formToPayload,
  payloadToForm,
  type RuleDraftPayload,
} from 'components/MapPage/CorporateViewTabs/EventsTab/ruleFormModel';
import {
  useCreateOrgEventAutomationRule,
  useOrgEventAutomationRules,
  useUpdateOrgEventAutomationRule,
} from 'hooks/orgEvents/orgEventAutomationHooks';
import { useAors } from 'hooks/useAors';
import {
  selectGroupGuid,
  selectUserGuid,
  useAppSelector,
} from 'slices/commonSelectors';
import { useAorText } from 'utils/aorTerminology';
import { RULE_TEMPLATES, type RuleTemplate } from '../ruleTemplates';

export function RuleStep() {
  const aorText = useAorText();
  const groupGuid = useAppSelector(selectGroupGuid);
  const userGuid = useAppSelector(selectUserGuid);
  const { data: aors = [] } = useAors();
  const { data: assets = [] } = useResourceSets();
  const { data: rules = [] } = useOrgEventAutomationRules();
  const createRule = useCreateOrgEventAutomationRule();
  const updateRule = useUpdateOrgEventAutomationRule();
  const [customizing, setCustomizing] = useState<RuleDraftPayload | null>(null);
  const [addingId, setAddingId] = useState<string | null>(null);
  const [enablingAll, setEnablingAll] = useState(false);

  const aorIds = useMemo(() => aors.map((aor) => aor.id), [aors]);
  const aorNameById = useMemo(
    () => new Map(aors.map((aor) => [aor.id, aor.name])),
    [aors]
  );
  const disabledRules = rules.filter((rule) => !rule.enabled && !rule.archived);
  const activeRules = rules.filter((rule) => !rule.archived);

  const blocker = (template: RuleTemplate): string | null => {
    if (template.needsAors && aors.length === 0) {
      return aorText('Add an AOR first.');
    }
    if (template.needsAssets && assets.length === 0) {
      return 'Import assets first.';
    }
    return null;
  };

  const addTemplate = async (template: RuleTemplate) => {
    if (!groupGuid) return;
    setAddingId(template.id);
    try {
      const payload = formToPayload(
        payloadToForm(template.build(aorIds)),
        aorNameById
      );
      await createRule.mutateAsync({
        ...payload,
        enabled: false,
        group_guid: groupGuid,
        created_by_user_guid: userGuid ?? null,
      });
      toast.success(`Added "${payload.name}" (off).`);
    } catch {
      toast.error('Could not add the rule.');
    } finally {
      setAddingId(null);
    }
  };

  const enableAll = async () => {
    setEnablingAll(true);
    try {
      for (const rule of disabledRules) {
        await updateRule.mutateAsync({ id: rule.id, enabled: true });
      }
    } catch {
      toast.error('Could not turn on every rule.');
    } finally {
      setEnablingAll(false);
    }
  };

  return (
    <div className="tw:flex tw:flex-col tw:gap-4">
      <div>
        <h3 className="tw:m-0 tw:text-sm tw:font-semibold">
          Create event automation rules
        </h3>
        <p className="tw:m-0 tw:text-sm tw:text-muted-foreground">
          {aorText(
            'Start from a template built on the analytics-ready hazard feeds. Rules are added turned off, so you can review them before they open events.'
          )}
        </p>
      </div>

      <ItemGroup className="tw:gap-2">
        {RULE_TEMPLATES.map((template) => {
          const reason = blocker(template);
          return (
            <Item key={template.id} variant="outline" size="sm">
              <ItemContent className="tw:min-w-0">
                <ItemTitle>{aorText(template.title)}</ItemTitle>
                <ItemDescription>
                  {reason ?? aorText(template.description)}
                </ItemDescription>
              </ItemContent>
              <ItemActions>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={!!reason}
                  onClick={() => setCustomizing(template.build(aorIds))}
                >
                  Customize
                </Button>
                <Button
                  type="button"
                  size="sm"
                  disabled={!!reason || addingId !== null}
                  onClick={() => void addTemplate(template)}
                >
                  {addingId === template.id ? 'Adding…' : 'Add'}
                </Button>
              </ItemActions>
            </Item>
          );
        })}
      </ItemGroup>

      {activeRules.length > 0 ? (
        <div className="tw:flex tw:flex-col tw:gap-2">
          <div className="tw:flex tw:items-center tw:justify-between">
            <h4 className="tw:m-0 tw:text-sm tw:font-semibold">
              Your rules ({activeRules.length})
            </h4>
            {disabledRules.length > 0 ? (
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={enablingAll}
                onClick={() => void enableAll()}
              >
                {enablingAll
                  ? 'Turning on…'
                  : `Turn on all (${disabledRules.length})`}
              </Button>
            ) : null}
          </div>
          <ItemGroup className="tw:gap-1">
            {activeRules.map((rule) => (
              <Item key={rule.id} variant="outline" size="xs">
                <ItemContent className="tw:min-w-0">
                  <ItemTitle className="tw:truncate">{rule.name}</ItemTitle>
                </ItemContent>
                <ItemActions>
                  <Switch
                    aria-label={`${rule.enabled ? 'Turn off' : 'Turn on'} ${rule.name}`}
                    checked={rule.enabled}
                    onCheckedChange={(checked) =>
                      updateRule.mutate({ id: rule.id, enabled: checked })
                    }
                  />
                </ItemActions>
              </Item>
            ))}
          </ItemGroup>
        </div>
      ) : null}

      <FullScreenDialog
        open={customizing !== null}
        onOpenChange={(open) => {
          if (!open) setCustomizing(null);
        }}
        title="Create automation rule"
        bodyClassName="tw:!flex tw:!flex-col tw:!overflow-hidden"
      >
        {customizing ? (
          <RuleEditorSteps
            rule={null}
            initialPayload={customizing}
            onClose={() => setCustomizing(null)}
          />
        ) : null}
      </FullScreenDialog>
    </div>
  );
}
